<template>
  <div :class="inputClass">
    <chat-input-audio
      v-if="customAudio"
      @send-message-audio="sendMessageAudio"
      class="input__button-recording"
      :disabled="isDisabledWhatsApp"
    />
    <div
      @click="focusInput(false)"
      :style="inputWrapperHeight"
      :class="{
        'is-focused': !disabled && !disabledWhatsapp && isFocused,
        disabled: disabled,
      }"
      class="input__block"
    >
      <vue-scroll ref="chatInputScroll">
        <div
          :style="placeholderVisibility"
          class="input__placeholder"
          :class="{ disabled: disabled }"
        >
          {{ $t("chat.input-reply") }}
        </div>
        <div
          ref="chatInput"
          @keydown.enter.exact.prevent
          @keydown.up.exact="moveUp"
          @keydown.down.exact="moveDown"
          @keyup.enter.exact="onEnter"
          @paste.prevent="onPaste"
          @input="onChange"
          @focus="onFocus"
          @blur="isFocused = false"
          @contextmenu.prevent.stop="menuOnClick"
          class="input__input"
          :contenteditable="!disabled && !isDisabledWhatsApp"
        ></div>
      </vue-scroll>
      <div class="input__actions">
        <input-button
          v-tooltip="'Emojis'"
          :active="emojiOpened"
          :disabled="disabled || isDisabledWhatsApp"
          @clicked="toggleEmoji"
        >
          <smile-icon class="input__icon" />
        </input-button>
        <input-button
          v-tooltip="'Multimedia'"
          :active="multimediaOpened"
          :disabled="disabled"
          @clicked="toggleMultimedia"
        >
          <plus-icon class="input__icon" />
        </input-button>
        <input-button
          v-if="showCancelButton"
          :disabled="disabled"
          classes="is-alert"
          @clicked="clickCancel"
        >
          <close-icon class="input__icon-cancel" />
        </input-button>
      </div>
    </div>

    <vue-simple-context-menu
      :element-id="'chat-input-context-menu'"
      :options="contextMenuOptions"
      :ref="'inputContextMenu'"
      @option-clicked="menuOptionClicked"
    />
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import {
  CHAT_EMOJI_TOGGLE,
  CHAT_IMAGE_FROM_CLIPBOARD,
  CHAT_INPUT_FOCUS,
  CHAT_MULTIMEDIA_TOGGLE,
  SHORTCUTS_MOVE_DOWN,
  SHORTCUTS_MOVE_UP,
  SHORTCUTS_SEARCH_TEXT,
  SHORTCUTS_SELECT_INDEX,
  SHORTCUTS_SELECT_TEXT,
  SHORTCUTS_TOGGLE,

} from '@/eventTypes';
import EventBus from '@/eventBus';
import CloseIcon from '@/assets/icons/close.svg';
import PlusIcon from '@/assets/icons/plus.svg';
import SmileIcon from '@/assets/icons/smile.svg';

export default {
  components: {
    CloseIcon,
    PlusIcon,
    SmileIcon,
    InputButton: () => import('@/components/InputButton.vue'),
    ChatInputAudio: () => import('@/components/ChatInputAudio.vue'),
  },
  props: {
    clientId: {
      required: true,
      type: String,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    showCancelButton: {
      default: false,
      type: Boolean,
    },
    channelType: {
      default: '',
      type: String,
    },
    blocked: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      inputHeight: 0,
      isFocused: false,
      modalText: '',
      multimediaOpened: false,
      emojiOpened: false,
      contextMenuOptions: [
        { name: `<b>${this.$t('format.bold')}</b>`, slug: 'bold' },
        { name: `<i>${this.$t('format.italic')}</i>`, slug: 'italic' },
        { name: this.$t('format.strike'), class: 'option-strikethrough', slug: 'strike' },
        { name: this.$t('format.mono'), class: 'option-monospace', slug: 'mono' },
      ],
      formattingChars: {
        bold: '*',
        italic: '_',
        strike: '~',
        mono: '`',
      },
      selectedCampaign: null,
    };
  },
  computed: {
    ...mapGetters([
      'chatCurrentInput',
      'disabledWhatsapp',
      'getConfig',
      'activeConversation',
      'activeConversationHistory',
      'getUserCampaigns',

    ]),
    ...mapState({
      currentCampaign: (state) => state.currentCampaign,
    }),
    text() {
      return this.modalText.trim();
    },
    isTextEmpty() {
      return this.text.length === 0;
    },
    placeholderVisibility() {
      return {
        visibility: this.isTextEmpty ? 'visible' : 'hidden',
      };
    },
    inputWrapperHeight() {
      const calculatedHeight = this.inputHeight > 88 ? `${110}px` : 'auto';

      return {
        height: calculatedHeight,
      };
    },
    openShortcuts() {
      if (!this.isTextEmpty) {
        return this.text.startsWith('/');
      }

      return false;
    },
    inputClass() {
      return [
        'input',
        {
          disabled: this.disabled,
          blocked: this.blocked,
        },
      ];
    },
    enableFormatting() {
      return ['whatsapp', 'telegram', 'messenger'].includes(this.channelType);
    },
    conversation() {
      return !this.isHistory
        ? this.activeConversation.conversation
        : this.activeConversationHistory.conversation;
    },
    campaign() {
      return this.conversation.campaign ?? {};
    },
    channel() {
      return this.conversation.channel ?? {};
    },
    campaignId() {
      return this.campaign.id ?? null;
    },
    channelId() {
      return this.channel.id ?? null;
    },
    configuration() {
      return this.getConfig(this.campaignId, this.channelId);
    },
    customAudio() {
      return this.configuration.customAudio ?? false;
    },
    customImages() {
      return this.configuration.customImages ?? false;
    },
    isDisabledWhatsApp() {
      return this.disabledWhatsapp;
    },
  },
  watch: {
    clientId() {
      this.setInputFromClient();
    },
    openShortcuts(newVal) {
      EventBus.$emit(SHORTCUTS_TOGGLE, newVal);
    },
    text(newVal) {
      this.setCurrentInput({
        clientId: this.clientId,
        currentInput: newVal,
      });
    },
  },
  created() {
    EventBus.$on(CHAT_INPUT_FOCUS, () => {
      this.focusInput();
    });

    EventBus.$on(SHORTCUTS_SELECT_TEXT, (text) => {
      this.setText(text.text);
      this.selectedCampaign = text.campaign;
      this.focusInput(true);
    });

    EventBus.$on(CHAT_MULTIMEDIA_TOGGLE, (opened) => {
      this.multimediaOpened = opened;
    });

    EventBus.$on(CHAT_EMOJI_TOGGLE, (opened) => {
      this.emojiOpened = opened;
    });
  },
  destroyed() {
    EventBus.$off(CHAT_INPUT_FOCUS);
    EventBus.$off(SHORTCUTS_SELECT_TEXT);
    EventBus.$off(CHAT_MULTIMEDIA_TOGGLE);
    EventBus.$off(CHAT_EMOJI_TOGGLE);
  },
  mounted() {
    this.focusInput();
  },
  methods: {
    ...mapMutations({
      setCurrentInput: 'updateConversationCurrentInput',
    }),
    setInputFromClient() {
      const clientInput = this.chatCurrentInput(this.clientId);
      this.setText(clientInput);
    },
    onPaste(e) {
      if (!this.disabled) {
        const text = e.clipboardData.getData('text/plain');

        if (text.length > 0) {
          document.execCommand('insertText', false, text);
          this.scrollToBottom();
        }

        if (this.customImages && e.clipboardData.files.length > 0) {
          const multimedia = e.clipboardData.files[0];

          if (multimedia.type.startsWith('image')) {
            const tempMessage = {
              text: null,
              multimedia,
              type: 'image',
            };

            EventBus.$emit(CHAT_IMAGE_FROM_CLIPBOARD, tempMessage);
          }
        }
      }
    },
    onChange(e) {
      if (!this.disabled) {
        this.modalText = e.target.innerText.trim();

        if (this.isTextEmpty) {
          this.setText('');
        }

        if (this.openShortcuts) {
          const searchText = this.text.substring(1);
          EventBus.$emit(SHORTCUTS_SEARCH_TEXT, searchText);
        }

        this.updateInputHeight();
      }
    },
    setText(text) {
      this.modalText = text;
      this.$refs.chatInput.innerHTML = text;
      this.updateInputHeight();
    },
    addEmoji(text) {
      this.modalText += text;
      this.$refs.chatInput.innerHTML += text;
      this.updateInputHeight();
      this.focusInput(true);
    },
    focusInput(moveCaret = false) {
      this.$nextTick(() => {
        if (this.$refs.chatInput) {
          if (moveCaret) {
            this.focusAndMoveCaret();
          } else {
            this.$refs.chatInput.focus();
          }
        }
      });
    },
    resetInput() {
      this.setText('');
      this.focusInput();
      EventBus.$emit(CHAT_EMOJI_TOGGLE, false);
    },
    onEnter() {
      if (!this.disabled) {
        if (this.openShortcuts) {
          EventBus.$emit(SHORTCUTS_SELECT_INDEX);
        } else {
          this.sendMessage();
        }
      }
    },
    sendMessage() {
      if (!this.isTextEmpty) {
        const msg = {
          text: this.text,
          type: 'text',
        };

        const campaign = this.selectedCampaign;

        this.$emit('send-message', { message: msg, campaign });
        this.resetInput();
      }
    },
    sendMessageAudio(msg) {
      this.$emit('send-message', msg);
    },
    scrollToBottom() {
      const { chatInputScroll } = this.$refs;

      if (chatInputScroll) {
        this.$nextTick(() => {
          chatInputScroll.scrollTo({ y: '100%' }, 0);
        });
      }
    },
    updateInputHeight() {
      this.inputHeight = this.$refs.chatInput.clientHeight;
    },
    toggleMultimedia() {
      EventBus.$emit(CHAT_EMOJI_TOGGLE, false);
      EventBus.$emit(CHAT_MULTIMEDIA_TOGGLE, !this.multimediaOpened);
    },
    toggleEmoji() {
      EventBus.$emit(CHAT_MULTIMEDIA_TOGGLE, false);
      EventBus.$emit(CHAT_EMOJI_TOGGLE, !this.emojiOpened);
    },
    moveUp(event) {
      if (this.openShortcuts) {
        event.preventDefault();
        EventBus.$emit(SHORTCUTS_MOVE_UP);
      }
    },
    moveDown(event) {
      if (this.openShortcuts) {
        event.preventDefault();
        EventBus.$emit(SHORTCUTS_MOVE_DOWN);
      }
    },
    clickCancel() {
      this.$emit('cancelled');
    },
    focusAndMoveCaret() {
      const target = this.$refs.chatInput;

      if (window.getSelection) {
        target.focus();
        const range = window.getSelection();
        range.selectAllChildren(target);
        range.collapseToEnd();
      } else if (document.selection) {
        const range = document.selection.createRange();
        range.moveToElementText(target);
        range.collapse(false);
        range.select();
      }
    },
    menuOnClick(event) {
      if (this.enableFormatting) {
        const selection = window.getSelection();
        let start;
        let end;

        if (selection.anchorOffset < selection.focusOffset) {
          start = selection.anchorOffset;
          end = selection.focusOffset;
        } else {
          start = selection.focusOffset;
          end = selection.anchorOffset;
        }

        const length = end - start;

        if (length > 0) {
          this.$refs.inputContextMenu.showMenu(event, {
            start,
            end,
          });
        }
      }
    },
    menuOptionClicked(event) {
      const selection = event.item;
      const option = event.option.slug;
      const textStart = this.modalText.slice(0, selection.start);
      const toReplace = this.modalText.slice(selection.start, selection.end);
      const textEnd = this.modalText.slice(selection.end);
      const char = this.wrapperCharacter(option);
      const replacedText = this.wrapText(toReplace, char);

      const resultingText = `${textStart}${replacedText}${textEnd}`;
      this.setText(resultingText);
      this.focusInput(true);
    },
    wrapText(text, char) {
      return `${char}${text.trim()}${char}`;
    },
    wrapperCharacter(slug) {
      const char = this.formattingChars[slug];
      let recurrence = 1;

      if (slug === 'mono' && this.channelType === 'whatsapp') {
        recurrence = 3;
      }

      return char.repeat(recurrence);
    },
    onFocus() {
      if (this.isHistory && !this.currentCampaign) {
        this.$refs.chatInput.blur();
      } else {
        this.isFocused = true;
        EventBus.$emit(CHAT_MULTIMEDIA_TOGGLE, false);
      }
    },

  },
};
</script>
